import { ColumnDef } from '@tanstack/react-table'
import { cn } from '@/libs/utils'
import { MoreHorizontal, Edit } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTableColumnHeader } from '@/components/crud'
import { Agency } from './schema'

export const createColumns = (
  t: (key: string) => string,
  onEdit?: (agency: Agency) => void
): ColumnDef<Agency>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t('common.selectAll')}
        className='translate-y-[2px]'
      />
    ),
    meta: {
      className: cn(
        'sticky md:table-cell left-0 z-10 rounded-tl',
        'bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted'
      ),
    },
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t('common.selectRow')}
        className='translate-y-[2px]'
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'avatarUrl',
    header: '',
    cell: ({ row }) => {
      const agency = row.original
      return (
        <Avatar className='h-8 w-8'>
          <AvatarImage
            src={agency.avatarUrl || ''}
            alt={agency.fullName || 'Agency'}
          />
          <AvatarFallback>
            {agency.firstName?.charAt(0) || agency.email?.charAt(0) || 'A'}
          </AvatarFallback>
        </Avatar>
      )
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'fullName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('agencies.agencyName')} />
    ),
    cell: ({ row }) => {
      const agency = row.original
      return (
        <div className='flex space-x-2'>
          <div>
            <div className='font-medium'>
              {agency.fullName ||
                `${agency.firstName || ''} ${agency.lastName || ''}`.trim()}
            </div>
            <div className='text-sm text-muted-foreground'>{agency.email}</div>
          </div>
        </div>
      )
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.status')} />
    ),
    cell: ({ row }) => {
      const status = row.getValue('status') as string
      return (
        <Badge
          variant={
            status === 'Active'
              ? 'default'
              : status === 'Suspended'
                ? 'destructive'
                : 'secondary'
          }
        >
          {status}
        </Badge>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
    enableSorting: true,
  },
  {
    accessorKey: 'phoneNumber',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('agencies.phone')} />
    ),
    cell: ({ row }) => {
      const phone = row.getValue('phoneNumber') as string
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[150px] truncate'>{phone || '-'}</span>
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: 'organizationId',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('agencies.organization')}
      />
    ),
    cell: ({ row }) => {
      const orgId = row.getValue('organizationId') as string
      return orgId ? (
        <span className='rounded bg-muted px-2 py-1 font-mono text-xs'>
          {orgId.slice(0, 8)}...
        </span>
      ) : (
        <span className='text-muted-foreground'>-</span>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.createdAt')} />
    ),
    cell: ({ row }) => {
      const date = row.getValue('createdAt') as string
      return (
        <div className='flex w-[100px] items-center'>
          <span>{date ? new Date(date).toLocaleDateString() : '-'}</span>
        </div>
      )
    },
    enableSorting: true,
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const agency = row.original
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>{t('common.openMenu')}</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit?.(agency)}>
              <Edit className='mr-2 h-4 w-4' />
              {t('common.edit')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
    enableSorting: false,
    enableHiding: false,
  },
]
